import { createContext, ReactNode, useContext, useEffect, useState, useMemo } from 'react';
import { useTheme } from 'react-admin';
import {
    defaultFundCorporations,
    defaultCompanySectors,
    defaultContactGender,
    defaultDealCategories,
    defaultDealPipelineStatuses,
    defaultDealStages,
    defaultNoteStatuses,
    defaultTaskTypes,
    defaultTitle,
} from './defaultConfiguration';
import { ContactGender, DealStage, NoteStatus } from '../types/types';

// Define types for the context value
export interface ConfigurationContextValue {
    fundCorporations: string[];
    companySectors: string[];
    dealCategories: string[];
    dealPipelineStatuses: string[];
    dealStages: DealStage[];
    noteStatuses: NoteStatus[];
    taskTypes: string[];
    title: string;
    contactGender: ContactGender[];
}

// Create context with undefined initial value to ensure proper context checking
export const ConfigurationContext = createContext<ConfigurationContextValue | undefined>(undefined);

export const ConfigurationProvider = ({ children }: { children: ReactNode }) => {
    const contextValue = useMemo<ConfigurationContextValue>(() => ({
        fundCorporations: defaultFundCorporations,
        companySectors: defaultCompanySectors,
        dealCategories: defaultDealCategories,
        dealPipelineStatuses: defaultDealPipelineStatuses,
        dealStages: defaultDealStages,
        noteStatuses: defaultNoteStatuses,
        taskTypes: defaultTaskTypes,
        title: defaultTitle,
        contactGender: defaultContactGender,
    }), []);

    return (
        <ConfigurationContext.Provider value={contextValue}>
            {children}
        </ConfigurationContext.Provider>
    );
};

export const useConfigurationContext = () => {
    const context = useContext(ConfigurationContext);
    if (!context) {
        throw new Error('useConfigurationContext must be used within a ConfigurationProvider');
    }
    return context;
};


// Add a new hook to get the logo based on current theme
export const useThemeLogo = () => {
    const [theme] = useTheme();
    return theme === 'dark' ? '/logos/logo_white.png' : '/logos/logo_black.png';
};

const useThemeChangeLogger = () => {
    const [theme, setTheme] = useTheme();

    useEffect(() => {
        console.log(`[DEBUG] Theme changed to: ${theme}`);
        // Perform additional actions here, such as updating a logo
    }, [theme]);

    return [theme, setTheme];
};